import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { CreateFormat, Format, FormatIcon, FormatList, SearchFormat, UpdateFormat } from '@celum/formats/domain';

import { FormatsContextAware } from './formats-context-aware';
import { NotificationService } from './notification.service';

export const FORMAT_SERVICE = new InjectionToken<FormatsService>('FORMAT_SERVICE');

/**
 * Single implementation of format-related API calls shared by the standalone `formats` app and the `formats-embed`
 * app. The environment-specific concerns (apiUrl/tenant resolution, authentication headers for embedded calls) are
 * handled by `FormatsContextAware` based on the `FORMATS_CONTEXT` provided where this service is registered.
 */
@Injectable()
export class FormatsService extends FormatsContextAware {
  public icons: FormatIcon[] = [];

  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);

  private static readonly LOAD_ERROR_KEY = 'FORMAT_LIST.SNACKBAR.LOAD_ERROR';

  constructor() {
    super();
    this.getFormatIcons().subscribe(icons => this.setIcons(icons));
  }

  private setIcons(icons: FormatIcon[] | null | undefined): void {
    const resolvedIcons = icons ?? [];
    const defaultIcon = resolvedIcons.find(icon => icon.id === 'default');
    this.icons = defaultIcon ? [defaultIcon, ...resolvedIcons.filter(icon => icon.id !== 'default')] : resolvedIcons;
  }

  public getFormatByKey(key: string): Observable<FormatList> {
    return this.withContext(context =>
      this.http.get<FormatList>(`${this.apiUrl}/formats`, { params: { formatKey: key }, context }).pipe(
        catchError(error => {
          console.error('FormatsService: error on getFormatByKey', error.error);
          this.notificationService.error(error.error?.errorMessage || FormatsService.LOAD_ERROR_KEY);
          return of({ content: [], totalElements: 0, hasNext: false } as FormatList);
        })
      )
    );
  }

  public getFormatById(formatId: number): Observable<Format> {
    return this.withContext(context =>
      this.http.get<Format>(`${this.apiUrl}/formats/${formatId}`, { context }).pipe(
        catchError(error => {
          console.error('FormatsService: error on getFormatById', error.error);
          this.notificationService.error(error.error?.errorMessage || FormatsService.LOAD_ERROR_KEY);
          return of(null);
        })
      )
    );
  }

  public deleteFormat(formatId: number): Observable<void> {
    return this.withContext(context =>
      this.http.delete<void>(`${this.apiUrl}/formats/${formatId}`, { context }).pipe(
        catchError(error => {
          console.error('FormatsService: error on deleteFormat', error.error);
          this.notificationService.error(error.error?.errorMessage || 'FORMAT_LIST.SNACKBAR.DELETE_ERROR');
          throw error;
        })
      )
    );
  }

  public createFormat(format: CreateFormat): Observable<Format> {
    return this.withContext(context =>
      this.http.post<Format>(`${this.apiUrl}/formats`, format, { context }).pipe(
        catchError(error => {
          console.error('FormatsService: error on format creation', error.error);
          this.notificationService.error(error.error?.errorMessage || 'FORMAT_LIST.SNACKBAR.CREATE_ERROR');
          return of(null);
        })
      )
    );
  }

  public updateFormat(formatId: number, format: UpdateFormat): Observable<Format> {
    return this.withContext(context =>
      this.http.put<Format>(`${this.apiUrl}/formats/${formatId}`, format, { context }).pipe(
        catchError(error => {
          console.error('FormatsService: error on format update', error.error);
          if (error.error?.errorKey === 'format.only-celum-admin-allowed') {
            this.notificationService.error('FORMAT_LIST.SNACKBAR.PROTECTED');
          } else {
            this.notificationService.error(error.error?.errorMessage || 'FORMAT_LIST.SNACKBAR.UPDATE_ERROR');
          }
          return of(null);
        })
      )
    );
  }

  public searchFormats(searchFormatDTO: SearchFormat): Observable<FormatList> {
    return this.withContext(context =>
      this.http.post<FormatList>(`${this.apiUrl}/formats/search`, searchFormatDTO, { context }).pipe(
        catchError(error => {
          console.error('FormatsService: error on format search', error.error);
          this.notificationService.error(error.error?.errorMessage || FormatsService.LOAD_ERROR_KEY);
          return of({ content: [], totalElements: 0, hasNext: false } as FormatList);
        })
      )
    );
  }

  public getFormatIcons(): Observable<FormatIcon[]> {
    return this.withContext(context =>
      this.http.get<FormatIcon[]>(`${this.apiUrl}/formats/icons`, { context }).pipe(
        map(icons => icons ?? []),
        catchError(error => {
          console.error('FormatsService: error when get format icons', error);
          this.notificationService.error(error.error?.errorMessage || FormatsService.LOAD_ERROR_KEY);
          return of([]);
        })
      )
    );
  }
}
